import React, { useEffect, useState } from "react";
import { Archive, BookOpen, CalendarDays, ChevronRight, ClipboardCheck, FileText, Flag, GraduationCap, MessagesSquare, NotebookPen, Route, Smartphone, Star, User2, Wallet, Briefcase, HandHeart } from "lucide-react";
import { reApi } from "@/lib/residentApi";
import ArrangeBoard from "@/components/nds/ArrangeBoard";

// Same look as the home-screen engine cards: colour bar, icon, title, short line, live status.
const TONES = {
  people: { bar: "from-rose-500 to-pink-300", icon: "text-rose-400" },
  ops: { bar: "from-cyan-500 to-cyan-300", icon: "text-cyan-400" },
  data: { bar: "from-violet-500 to-fuchsia-400", icon: "text-violet-400" },
  financial: { bar: "from-emerald-500 to-emerald-300", icon: "text-emerald-400" },
  comms: { bar: "from-amber-500 to-yellow-300", icon: "text-amber-400" },
  content: { bar: "from-sky-500 to-indigo-300", icon: "text-sky-400" },
  system: { bar: "from-brand-blue to-brand-cyan", icon: "text-brand-cyan" },
};

const META = {
  profile: [User2, "people", "Personal details, contacts, housing and intake"],
  lifepass: [Smartphone, "system", "Their live PACT Life Pass — use it exactly as the resident does"],
  vocation: [Briefcase, "ops", "Employer, trade track, readiness and job placements"],
  roadmap: [Route, "data", "C2C baseline roadmap — where they are and where they're going"],
  ged: [GraduationCap, "content", "GED focus subject, practice scores, milestones and exam readiness"],
  calendar: [CalendarDays, "ops", "Upcoming and past sessions on their schedule"],
  lifeskills: [GraduationCap, "data", "Life skills chapters, lessons and homework"],
  financial: [Wallet, "financial", "Balances, paychecks, savings, expenses and ledger"],
  notes: [NotebookPen, "comms", "Case notes written by staff"],
  memos: [MessagesSquare, "comms", "Campus memo threads with the resident"],
  checkins: [ClipboardCheck, "ops", "Room ready, schedule and wellness check-ins"],
  phase: [Flag, "people", "Program phase and phase change history"],
  documents: [FileText, "content", "Signed forms, IDs and uploaded files"],
  reviews: [Star, "content", "Weekly and AI-drafted progress reviews"],
  impact: [HandHeart, "financial", "Service value delivered through verified pillars (not cash)"],
  archive: [Archive, "system", "Archived records for this resident"],
};

export default function ResidentCards({ residentId, tabs, onOpen }) {
  const [live, setLive] = useState({});
  const [failed, setFailed] = useState(false);
  useEffect(() => { reApi.get(`/residents/${residentId}/cards`).then(setLive).catch(() => setFailed(true)); }, [residentId]);
  return (
    <div data-testid="resident-cards">
      <ArrangeBoard board="resident_file" ids={tabs.map(([id]) => id)} labels={Object.fromEntries(tabs.map(([id, l]) => [id, l]))} renderTile={(id) => {
        const [, label] = tabs.find(([x]) => x === id);
        const [Icon, tone, desc] = META[id] || [BookOpen, "system", ""];
        const s = live[id];
        return <CardTile key={id} testid={`resident-card-${id}`} label={label} desc={desc} Icon={Icon} tone={tone} onOpen={() => onOpen(id)}
          stat={s ? s.stat : failed ? "—" : "…"} note={s?.note} alert={s?.alert} />;
      }} />
    </div>
  );
}

/** One tile in the platform card style (colour bar, icon, title, short line, live stat). */
export function CardTile({ testid, label, desc, Icon, tone = "system", stat, note, alert, onOpen }) {
  const t = TONES[tone] || TONES.system;
  return (
    <button type="button" onClick={onOpen} data-testid={testid}
      className="nds-cap-tile relative overflow-hidden bg-[rgba(17,26,46,0.75)] border border-white/[0.08] shadow-[0_8px_32px_0_rgba(0,0,0,0.37)] text-left pt-4 pb-3 px-4 flex flex-col gap-2 hover:-translate-y-0.5 active:scale-[0.99] transition-transform duration-300">
      <span className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${t.bar}`} />
      <div className="flex items-start justify-between gap-2">
        <div className={`w-8 h-8 grid place-items-center rounded-lg border border-slate-800 bg-navy-950 ${t.icon}`}><Icon className="w-3.5 h-3.5" strokeWidth={1.8} /></div>
        <ChevronRight className="h-4 w-4 text-slate-500" />
      </div>
      <div className="font-serif text-[0.95rem] font-bold text-slate-100 leading-tight">{label}</div>
      <p className="text-xs text-slate-400 leading-snug">{desc}</p>
      <div data-testid={`${testid}-live`} className="border-t border-white/5 pt-2 flex items-baseline gap-2 min-w-0">
        <span className={`font-mono text-sm ${alert ? "text-amber-300" : "text-slate-100"} shrink-0`}>{stat}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500 truncate">{note || ""}</span>
      </div>
    </button>
  );
}
